import { useEffect, useRef, useState } from 'react';
import { Alert, Spin, Modal } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { getOrg } from 'src/_shared/services/claus/endpoints';
import ConsoleHeader from '../console/ConsoleHeader.jsx';
import IntegrationList from './IntegrationList.jsx';
import CreateIntegrationWizard from './CreateIntegrationWizard.jsx';
import IntegrationEditor from './IntegrationEditor.jsx';
import './integrations.css';

// By convention the company's CLAUS organization id == ERIN companyId (preserved by create-with-id
// provisioning). We resolve read-only on load; the org is provisioned lazily when the operator
// creates their first integration (so merely browsing never creates orgs).
export default function IntegrationsDashboard({ currentUser, company, tieredBonuses }) {
	const companyId = company?.id || currentUser?.companyId;
	const orgId = companyId; // canonical: org id == companyId

	const [resolving, setResolving] = useState(true);
	const [orgExists, setOrgExists] = useState(false);
	const [fatal, setFatal] = useState(null);
	const [view, setView] = useState({ mode: 'list' });

	useEffect(() => {
		let alive = true;
		(async () => {
			setResolving(true);
			setFatal(null);
			try {
				await getOrg(orgId);
				if (alive) setOrgExists(true);
			} catch (e) {
				// A missing org is expected for a not-yet-onboarded company — not fatal.
				if (/\b404\b|not found/i.test(e.message || '')) {
					if (alive) setOrgExists(false);
				} else if (alive) {
					setFatal(e.message || 'Could not reach the integrations service.');
				}
			} finally {
				if (alive) setResolving(false);
			}
		})();
		return () => {
			alive = false;
		};
	}, [orgId]);

	// The editor reports whether either of its tabs has unsaved edits; the crumb guards against
	// silently discarding them (the wizard keeps its own unguarded Cancel).
	const editorDirtyRef = useRef(false);
	const backToList = () => {
		editorDirtyRef.current = false;
		setView({ mode: 'list' });
	};
	const requestBackToList = () => {
		if (view.mode === 'edit' && editorDirtyRef.current) {
			Modal.confirm({
				title: 'Discard unsaved changes?',
				content: 'This editor has changes that haven’t been saved. Leave and discard them?',
				okText: 'Discard',
				okButtonProps: { danger: true },
				onOk: backToList,
			});
		} else {
			backToList();
		}
	};

	// Each view renders its own editorial page head (crumb chip + H1); the shell only provides the
	// page canvas and the view state machine.
	return (
		<div className="erin-jobsync erin-integrations">
			<div className="ei-page">
				{fatal && <Alert type="error" showIcon message="Integrations unavailable" description={fatal} />}

				{!fatal && resolving && (
					<div className="js-loading">
						<Spin />
					</div>
				)}

				{!fatal && !resolving && view.mode === 'list' && (
					<>
						<ConsoleHeader
							title="Integrations"
							subtitle={`${company?.name || 'Your company'} · ATS source & processing`}
							description="Connect ATS sources and control how their records become ERIN jobs."
							rightSlot={
								<button type="button" className="js-btn js-btn-primary" onClick={() => setView({ mode: 'create' })}>
									<PlusOutlined /> Create integration
								</button>
							}
						/>
						<IntegrationList
							orgId={orgId}
							orgExists={orgExists}
							company={company}
							onCreate={() => setView({ mode: 'create' })}
							onEdit={(id) => setView({ mode: 'edit', id })}
						/>
					</>
				)}

				{!fatal && !resolving && view.mode === 'create' && (
					<CreateIntegrationWizard
						orgId={orgId}
						company={company}
						onCancel={backToList}
						onCreated={(id) => {
							setOrgExists(true);
							setView({ mode: 'edit', id });
						}}
					/>
				)}

				{!fatal && !resolving && view.mode === 'edit' && (
					<IntegrationEditor
						orgId={orgId}
						id={view.id}
						currentUser={currentUser}
						company={company}
						tieredBonuses={tieredBonuses}
						onBack={requestBackToList}
						onDirtyChange={(d) => {
							editorDirtyRef.current = d;
						}}
					/>
				)}
			</div>
		</div>
	);
}
